import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const StudentSidebar = ({ 
  isOpen, 
  onClose, 
  students, 
  currentSubmissionId, 
  assignment,
  sortKey,
  sortDir
}) => {
  // Find the index of the current submission in the students array
  const currentIndex = students.findIndex(student => student.id === parseInt(currentSubmissionId, 10));
  const scrollContainerRef = useRef(null); // Ref for the scrollable list container

  // Effect to scroll the current student into view
  useEffect(() => {
    if (isOpen && currentSubmissionId && scrollContainerRef.current) {
      const currentStudentElement = document.getElementById(`student-sidebar-item-${currentSubmissionId}`);
      if (currentStudentElement) {
        // Check if the element is already fully visible
        const containerRect = scrollContainerRef.current.getBoundingClientRect();
        const elementRect = currentStudentElement.getBoundingClientRect();

        const isVisible = 
          elementRect.top >= containerRect.top &&
          elementRect.bottom <= containerRect.bottom;

        // Only scroll if the element isn't already fully visible
        if (!isVisible) {
          currentStudentElement.scrollIntoView({
            behavior: 'instant', // Use instant scrolling
            block: 'center'    // Scroll minimal distance to bring into view
          });
        }
      }
    }
  }, [currentSubmissionId, students, isOpen]); // Re-run when selection, list, or visibility changes

  return (
    <div 
      className={`fixed top-0 left-0 h-full w-80 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      style={{ borderRight: '1px solid #e5e7eb' }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-b from-white to-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">Students</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        
        {/* Students list */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
          {students.map((student, index) => {
            // Determine if this is the current student
            const isCurrent = student.id === parseInt(currentSubmissionId, 10);
            
            // Special styling for different student types
            const isUnmatched = student.first_name?.trim() === 'Unmatched Submission' || 
                             (student.full_name && student.full_name.includes('Unmatched Submission'));
            const isNotSubmitted = student.status === 'Not submitted';
            
            // Create URL for student's submission
            const studentUrl = student.status !== 'Not submitted' && student.status !== 'Grading' && student.id
              ? `/tclasses/${assignment?.course_id}/tassignments/${assignment?.id}/tsubmissions/${student.id}/tdetails?sortKey=${sortKey || ''}&sortDir=${sortDir || 'asc'}`
              : null;
            
            return (
              <div 
                key={student.id || `student-${index}`}
                id={`student-sidebar-item-${student.id}`} // Add unique ID for scrolling
                className={`relative border-b border-gray-100 transition-colors ${isCurrent ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              >
                {studentUrl ? (
                  <Link to={studentUrl} className="block">
                    <div className={`p-3 flex items-center ${isNotSubmitted ? 'opacity-60' : ''}`}>
                      {/* Position indicator */}
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 mr-3">
                        <span className="text-xs font-medium text-gray-600">{index + 1}</span>
                      </div>
                      
                      {/* Student info */}
                      <div className="flex-1">
                        <div className={`font-medium ${isUnmatched ? 'text-red-600 px-2 py-0.5 bg-red-50 border border-red-200 rounded-md shadow-sm' : 'text-gray-800'}`}>
                          {isUnmatched ? 'Unmatched Submission' : (
                            <>
                              <span>{student.first_name}</span>{' '}
                              <span>{student.last_name}</span>
                            </>
                          )}
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <div className="text-sm text-gray-500">
                            {student.status || 'Unknown status'}
                          </div>
                          <div className={`text-sm font-medium ${
                            isNotSubmitted ? 'text-gray-400' : 
                            (() => {
                              // Calculate percentage of max score
                              const maxScore = assignment?.max_score || 100;
                              const percentage = student.grade !== null && student.grade !== undefined && maxScore > 0 
                                ? (student.grade / maxScore) * 100 
                                : 0;
                              
                              return percentage >= 90 ? 'text-green-600' :
                                     percentage >= 80 ? 'text-green-500' :
                                     percentage >= 70 ? 'text-amber-500' :
                                     percentage >= 60 ? 'text-amber-600' :
                                     'text-amber-700';
                            })()
                          }`}>
                            {student.grade !== null && student.grade !== undefined 
                              ? `${student.grade}/${assignment?.max_score || 100}`
                              : (student.status === 'Not submitted' ? 'Not submitted' : 'Not graded')}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Current indicator - changed to a left border accent with a more subtle color */}
                    {isCurrent && (
                      <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-12 bg-gray-500 rounded-r-md"></div>
                    )}
                  </Link>
                ) : (
                  <div className={`p-3 flex items-center ${isNotSubmitted ? 'opacity-60' : ''}`}>
                    {/* Position indicator */}
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 mr-3">
                      <span className="text-xs font-medium text-gray-600">{index + 1}</span>
                    </div>
                    
                    {/* Student info */}
                    <div className="flex-1">
                      <div className={`font-medium ${isUnmatched ? 'text-red-600 px-2 py-0.5 bg-red-50 border border-red-200 rounded-md shadow-sm' : 'text-gray-800'}`}>
                        {isUnmatched ? 'Unmatched Submission' : (
                          <>
                            <span>{student.first_name}</span>{' '}
                            <span>{student.last_name}</span>
                          </>
                        )}
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <div className="text-sm text-gray-500">
                          {student.status || 'Unknown status'}
                        </div>
                        <div className="text-sm font-medium text-gray-400">
                          {student.status === 'Not submitted' ? 'Not submitted' : 'Not graded'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Footer with current position */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-center text-sm text-gray-600">
            {currentIndex !== -1 ? (
              <>
                <span className="font-medium text-gray-700">{currentIndex + 1}</span> of <span className="font-medium">{students.length}</span> students
              </>
            ) : (
              'Student not found in list'
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentSidebar;